import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import client from "@/lib/api";
import { Plus, Trash2, X } from "lucide-react";

const EMPTY = {
    slug: "",
    category: "breakfast",
    name: "",
    description: "",
    price: 0,
    image_url: "",
    modifiers: [],
    featured: false,
    available: true,
    available_date: "",
};

const slugify = (s) =>
    (s || "")
        .toLowerCase()
        .trim()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");

/**
 * Reusable dialog for CREATE and EDIT.
 * mode: "create" | "edit"
 */
export default function ItemFormDialog({ open, onOpenChange, mode, item, categories, onSaved, isSpecial = false }) {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (mode === "edit" && item) {
            setForm({
                slug: item.slug || "",
                category: item.category || "breakfast",
                name: item.name || "",
                description: item.description || "",
                price: item.price ?? 0,
                image_url: item.image_url || "",
                modifiers: JSON.parse(JSON.stringify(item.modifiers || [])),
                featured: !!item.featured,
                available: isSpecial ? item.active !== false : item.available !== false,
                available_date: item.available_date || "",
            });
        } else {
            setForm({ ...EMPTY, category: categories[0]?.slug || "breakfast" });
        }
        setShowAdvanced(false);
    }, [open, mode, item, categories, isSpecial]);

    const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

    const setModifiers = (fn) => setForm((f) => ({ ...f, modifiers: fn(f.modifiers) }));

    const addGroup = () => setModifiers((m) => [...m, { name: "New group", required: false, multi: true, options: [] }]);
    const removeGroup = (i) => setModifiers((m) => m.filter((_, idx) => idx !== i));
    const updateGroup = (i, key, val) => setModifiers((m) => m.map((g, idx) => (idx === i ? { ...g, [key]: val } : g)));
    const addOption = (i) => setModifiers((m) => m.map((g, idx) => (idx === i ? { ...g, options: [...g.options, { name: "", price: 0 }] } : g)));
    const removeOption = (i, j) => setModifiers((m) => m.map((g, idx) =>
        idx === i ? { ...g, options: g.options.filter((_, k) => k !== j) } : g));
    const updateOption = (i, j, key, val) => setModifiers((m) => m.map((g, idx) =>
        idx === i ? { ...g, options: g.options.map((o, k) => (k === j ? { ...o, [key]: val } : o)) } : g));

    const isDrink = useMemo(() => ["cool-drinks", "hot-drinks"].includes(form.category), [form.category]);

    const submit = async () => {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        if (form.price === "" || form.price === null || Number.isNaN(Number(form.price))) {
            toast.error("Price is required"); return;
        }

        const payload = {
            ...form,
            price: Number(form.price),
            slug: mode === "create" ? (form.slug || slugify(form.name)) : form.slug,
            // ensure numeric option prices
            modifiers: form.modifiers.map((g) => ({
                ...g,
                options: g.options.map((o) => ({ ...o, price: Number(o.price || 0) })),
            })),
        };

        setSaving(true);
        try {
            if (isSpecial) {
                const body = {
                    section: payload.category, name: payload.name, description: payload.description,
                    price: payload.price, image_url: payload.image_url, modifiers: payload.modifiers,
                    featured: payload.featured, active: payload.available, available_date: payload.available_date || "",
                };
                if (mode === "create") await client.post("/admin/specials", body);
                else await client.patch(`/admin/specials/${item.id}`, body);
                toast.success(mode === "create" ? `Added ${payload.name}` : "Special saved");
            } else if (mode === "create") {
                await client.post("/admin/menu", payload);
                toast.success(`Added ${payload.name}`);
            } else {
                await client.patch(`/admin/menu/${item.id}`, {
                    name: payload.name,
                    description: payload.description,
                    price: payload.price,
                    image_url: payload.image_url,
                    category: payload.category,
                    featured: payload.featured,
                    available: payload.available,
                    modifiers: payload.modifiers,
                });
                toast.success("Item saved");
            }
            onOpenChange(false);
            if (onSaved) await onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const label = "text-xs uppercase tracking-wider text-brand-gold";
    const input = "w-full mt-1 bg-brand-ink border border-brand-line px-3 py-2 rounded-sm text-brand-cream focus:outline-none focus:border-brand-olive text-sm";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl bg-brand-charcoal border border-brand-line text-brand-cream rounded-sm max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">
                        {mode === "create" ? (isSpecial ? "Add New Special" : "Add New Menu Item") : `Edit — ${item?.name || ""}`}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-4">
                        <div>
                            <label className={label}>Name</label>
                            <input
                                data-testid="item-form-name"
                                className={input}
                                value={form.name}
                                onChange={(e) => setField("name", e.target.value)}
                                placeholder="e.g. Chicken Wrap"
                            />
                        </div>
                        <div>
                            <label className={label}>Price (R)</label>
                            <input
                                data-testid="item-form-price"
                                type="number"
                                step="0.01"
                                className={input}
                                value={form.price}
                                onChange={(e) => setField("price", e.target.value)}
                            />
                        </div>
                    </div>

                    <div>
                        <label className={label}>Description</label>
                        <textarea
                            data-testid="item-form-description"
                            rows={3}
                            className={`${input} resize-none`}
                            value={form.description}
                            onChange={(e) => setField("description", e.target.value)}
                            placeholder="Short description shown under the item name…"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-4">
                        <div>
                            <label className={label}>{isSpecial ? "Section" : "Category"}</label>
                            <select
                                data-testid="item-form-category"
                                className={input}
                                value={form.category}
                                onChange={(e) => setField("category", e.target.value)}
                            >
                                {categories.map((c) => (
                                    <option key={c.slug} value={c.slug}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={label}>Image URL <span className="text-brand-mute normal-case">(or use drag-drop on the row)</span></label>
                            <input
                                data-testid="item-form-image-url"
                                className={input}
                                value={form.image_url}
                                onChange={(e) => setField("image_url", e.target.value)}
                                placeholder="https://…"
                            />
                        </div>
                    </div>

                    {isSpecial && (
                        <div>
                            <label className={label}>Only show on date <span className="text-brand-mute normal-case">(leave empty to show every day)</span></label>
                            <input
                                data-testid="item-form-available-date"
                                type="date"
                                className={input}
                                value={form.available_date}
                                onChange={(e) => setField("available_date", e.target.value)}
                            />
                        </div>
                    )}

                    {mode === "create" && !isSpecial && (
                        <div>
                            <label className={label}>Slug <span className="text-brand-mute normal-case">(auto-generated from name — used for filename matching on bulk upload)</span></label>
                            <input
                                data-testid="item-form-slug"
                                className={input}
                                value={form.slug}
                                onChange={(e) => setField("slug", slugify(e.target.value))}
                                placeholder={slugify(form.name) || "chicken-wrap"}
                            />
                        </div>
                    )}

                    <div className="flex flex-wrap gap-6 pt-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <Switch checked={form.available} onCheckedChange={(v) => setField("available", v)} />
                            <span className="text-sm">{isSpecial ? "Active (shown on site)" : "Available"}</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <Switch checked={form.featured} onCheckedChange={(v) => setField("featured", v)} />
                            <span className="text-sm">Featured (Signature)</span>
                        </label>
                    </div>

                    {/* D. MODIFIERS EDITOR */}
                    <div className="border-t border-brand-line pt-4">
                        <button
                            type="button"
                            onClick={() => setShowAdvanced((v) => !v)}
                            className="text-sm text-brand-gold hover:underline"
                        >
                            {showAdvanced ? "▾" : "▸"} Advanced — Modifiers & Add-ons ({form.modifiers.length} group{form.modifiers.length === 1 ? "" : "s"})
                        </button>
                        {showAdvanced && (
                            <div className="mt-4 space-y-4">
                                {isDrink && (
                                    <p className="text-xs text-brand-mute italic">
                                        Drinks category — items in Cold/Hot Drinks skip the global Add-ons group.
                                    </p>
                                )}
                                {form.modifiers.map((g, i) => (
                                    <div key={i} className="border border-brand-line rounded-sm p-4 space-y-2">
                                        <div className="flex items-center justify-between gap-3">
                                            <input
                                                className={`${input} mt-0 flex-1`}
                                                value={g.name}
                                                onChange={(e) => updateGroup(i, "name", e.target.value)}
                                                placeholder="Group name (e.g. Add-ons)"
                                            />
                                            <button onClick={() => removeGroup(i)} className="text-brand-mute hover:text-brand-gold p-2" title="Remove group">
                                                <X size={16} />
                                            </button>
                                        </div>
                                        <div className="flex gap-4">
                                            <label className="flex items-center gap-2 text-xs cursor-pointer">
                                                <Switch checked={!!g.required} onCheckedChange={(v) => updateGroup(i, "required", v)} />
                                                Required
                                            </label>
                                            <label className="flex items-center gap-2 text-xs cursor-pointer">
                                                <Switch checked={!!g.multi} onCheckedChange={(v) => updateGroup(i, "multi", v)} />
                                                Allow multiple choices
                                            </label>
                                        </div>
                                        <div className="space-y-1.5">
                                            {g.options.map((o, j) => (
                                                <div key={j} className="grid grid-cols-[1fr_100px_32px] gap-2">
                                                    <input
                                                        className={`${input} mt-0`}
                                                        value={o.name}
                                                        onChange={(e) => updateOption(i, j, "name", e.target.value)}
                                                        placeholder="Option name"
                                                    />
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        className={`${input} mt-0`}
                                                        value={o.price}
                                                        onChange={(e) => updateOption(i, j, "price", e.target.value)}
                                                        placeholder="+R"
                                                    />
                                                    <button onClick={() => removeOption(i, j)} className="text-brand-mute hover:text-brand-gold">
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => addOption(i)}
                                            className="text-xs text-brand-gold inline-flex items-center gap-1 mt-1 hover:underline"
                                        >
                                            <Plus size={12} /> Add option
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={addGroup}
                                    className="text-sm text-brand-gold border border-brand-gold/40 rounded-sm px-3 py-1.5 inline-flex items-center gap-2 hover:bg-brand-gold hover:text-brand-ink transition-colors"
                                >
                                    <Plus size={14} /> Add modifier group
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="btn-ghost"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="item-form-save"
                        onClick={submit}
                        disabled={saving}
                        className="btn-primary"
                    >
                        {saving ? "Saving…" : mode === "create" ? "Create item" : "Save changes"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
